import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Globe, FileCheck, MapPin, Phone, Mail, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIES_OPTIONS = [
  { value: "canine", label: "Dogs", icon: "🐕" },
  { value: "feline", label: "Cats", icon: "🐱" },
  { value: "equine", label: "Horses", icon: "🐴" },
  { value: "cattle", label: "Cattle", icon: "🐄" },
  { value: "exotics", label: "Exotics", icon: "🦜" },
  { value: "others", label: "Farm Animals", icon: "🐖" },
];

const ShelterOnboarding = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    organization_name: "",
    description: "",
    website_url: "",
    ein: "",
    location_city: "",
    location_state: "",
    location_zip: "",
    contact_phone: "",
    contact_email: user?.email || "",
    species: [],
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecies = (species) => {
    setFormData(prev => ({
      ...prev,
      species: prev.species.includes(species)
        ? prev.species.filter(s => s !== species)
        : [...prev.species, species]
    }));
  };

  const validateStep1 = () => {
    if (!formData.organization_name.trim()) {
      toast.error("Organization name is required");
      return false;
    }
    if (!formData.website_url.trim() && !formData.ein.trim()) {
      toast.error("Please provide either a website URL or EIN for verification");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.location_city.trim() || !formData.location_state.trim() || !formData.location_zip.trim()) {
      toast.error("Please complete all location fields");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (formData.species.length === 0) {
      toast.error("Please select at least one species");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.post(`${API}/api/shelter/onboard`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Shelter profile created successfully!");
      navigate("/dashboard/shelter");
    } catch (err) {
      logger.error("Shelter onboarding failed:", err);
      toast.error(err.response?.data?.detail || "Failed to create shelter profile");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(prev => prev + 1);
  };

  const prevStep = () => setStep(prev => prev - 1);

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#050505]">
      <div className="max-w-2xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F472B6]/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-[#F472B6]" />
          </div>
          <h1 className="text-3xl md:text-4xl text-[#EDEDED] mb-2">Register Your Shelter</h1>
          <p className="text-[#A1A1AA]">Help animals find their forever homes on Breedling</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? "bg-[#F472B6] text-black" : "bg-[#27272A] text-[#71717A]"
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-[#F472B6]" : "bg-[#27272A]"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Organization Info */}
        {step === 1 && (
          <div className="glass-card p-6">
            <h2 className="text-xl text-[#EDEDED] mb-6">Organization Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Organization Name *</label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => handleChange("organization_name", e.target.value)}
                  placeholder="e.g., Happy Tails Rescue"
                  className="input-minimal"
                  data-testid="org-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Tell us about your rescue organization..."
                  rows={3}
                  className="input-minimal"
                />
              </div>

              <div className="p-4 rounded-lg bg-[#27272A]/50 border border-[#3F3F46]">
                <p className="text-sm text-[#A1A1AA] mb-3">
                  <strong className="text-[#EDEDED]">Verification Required:</strong> Provide at least one of the following
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                      <Globe className="w-4 h-4" /> Website URL
                    </label>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => handleChange("website_url", e.target.value)}
                      placeholder="https://yourrescue.org"
                      className="input-minimal"
                      data-testid="website-input"
                    />
                  </div>
                  
                  <div className="text-center text-sm text-[#71717A]">— OR —</div>
                  
                  <div>
                    <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                      <FileCheck className="w-4 h-4" /> EIN (Tax ID)
                    </label>
                    <input
                      type="text"
                      value={formData.ein}
                      onChange={(e) => handleChange("ein", e.target.value)}
                      placeholder="XX-XXXXXXX"
                      className="input-minimal"
                      data-testid="ein-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={nextStep}
                className="px-6 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors"
                data-testid="next-step-btn"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Location & Contact */}
        {step === 2 && (
          <div className="glass-card p-6">
            <h2 className="text-xl text-[#EDEDED] mb-6">Location & Contact</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-[#F472B6]" />
                <span className="text-sm text-[#A1A1AA]">Location</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-[#A1A1AA] mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.location_city}
                    onChange={(e) => handleChange("location_city", e.target.value)}
                    placeholder="City"
                    className="input-minimal"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">State *</label>
                  <input
                    type="text"
                    value={formData.location_state}
                    onChange={(e) => handleChange("location_state", e.target.value)}
                    placeholder="TX"
                    className="input-minimal"
                    maxLength={2}
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    value={formData.location_zip}
                    onChange={(e) => handleChange("location_zip", e.target.value)}
                    placeholder="12345"
                    className="input-minimal"
                    data-testid="zip-input"
                  />
                </div>
              </div>

              <div className="h-px bg-white/10 my-4" />

              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-[#F472B6]" />
                <span className="text-sm text-[#A1A1AA]">Contact Information</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange("contact_phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange("contact_email", e.target.value)}
                    placeholder="contact@rescue.org"
                    className="input-minimal"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={prevStep}
                className="px-6 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="px-6 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Species */}
        {step === 3 && (
          <div className="glass-card p-6">
            <h2 className="text-xl text-[#EDEDED] mb-6">What Animals Do You Rescue?</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SPECIES_OPTIONS.map((species) => (
                <button
                  key={species.value}
                  type="button"
                  onClick={() => toggleSpecies(species.value)}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    formData.species.includes(species.value)
                      ? "border-[#F472B6] bg-[#F472B6]/10"
                      : "border-[#3F3F46] hover:border-[#71717A]"
                  }`}
                  data-testid={`species-${species.value}`}
                >
                  <span className="text-2xl mb-2 block">{species.icon}</span>
                  <span className={`text-sm ${
                    formData.species.includes(species.value) ? "text-[#F472B6]" : "text-[#EDEDED]"
                  }`}>
                    {species.label}
                  </span>
                  {formData.species.includes(species.value) && (
                    <Check className="w-4 h-4 text-[#F472B6] inline ml-2" />
                  )}
                </button>
              ))}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={prevStep}
                className="px-6 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Complete Registration"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 rounded-lg bg-[#18181B] border border-[#27272A]">
          <h3 className="text-[#EDEDED] font-medium mb-2">Why Register?</h3>
          <ul className="text-sm text-[#A1A1AA] space-y-1">
            <li>• List animals for adoption on Breedling's platform</li>
            <li>• Reach thousands of potential adopters</li>
            <li>• Free for verified shelters and rescues</li>
            <li>• Set your own rehoming fees</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShelterOnboarding;
